import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { DesignQuestionnaire as DQType, QuestionnaireSelection, QuestionnaireSpecs, QuestionnaireVideo } from '../../types';
import { db, storage } from '../../utils/firebase';
import { collection, query, where, getDocs, doc, setDoc, updateDoc, addDoc, getDoc, Timestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { Heart, MessageSquare, ChevronLeft, ChevronRight, Video, StopCircle, Play, Trash2, Send, Ruler, ArrowLeft, Upload, X, ZoomIn, Loader2, TreePine, Palette, BookOpen, Sparkles } from 'lucide-react';
import LoadingScreen from '../ui/LoadingScreen';
import { logPortalActivity } from '../../utils/portalActivityLogger';
import { savePortalQuestionnaireStatus } from '../../utils/portalPinService';
import { DEFAULT_FRAME_STYLES, type FrameStyleOption } from '../../utils/questionnaireFrameStyles';
import { DEFAULT_JOINERY_STYLES, type JoineryStyleOption } from '../../utils/questionnaireJoineryStylesData';
import {
  DEFAULT_TIMBER_SPECIES,
  type QuestionnaireTimberSpecies,
} from '../../utils/questionnaireTimberSpeciesData';
import './DesignQuestionnaire.css';

// ═══════════════════════════════════════════
// STATIC QUESTIONNAIRE CONTENT
// ═══════════════════════════════════════════

// ═══════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════

interface DesignQuestionnaireProps {
  portalId: string;
  leadId: string;
  clientName: string;
  projectId?: string;
  isPublicLead?: boolean;
  onBack?: () => void;
  /** Stakeholder magic-link session — browsing only; no writes */
  stakeholderViewOnly?: boolean;
}

const DesignQuestionnaire: React.FC<DesignQuestionnaireProps> = ({
  portalId, leadId, clientName, projectId, isPublicLead, onBack, stakeholderViewOnly = false,
}) => {
  const [questionnaire, setQuestionnaire] = useState<DQType | null>(null);
  const [questionnaireDocId, setQuestionnaireDocId] = useState<string | null>(null);
  const [selections, setSelections] = useState<QuestionnaireSelection[]>([]);
  const [specs, setSpecs] = useState<QuestionnaireSpecs>({});
  const [videos, setVideos] = useState<QuestionnaireVideo[]>([]);
  const [generalNotes, setGeneralNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [frameStyles, setFrameStyles] = useState<FrameStyleOption[]>(DEFAULT_FRAME_STYLES);
  const [joineryStyles, setJoineryStyles] = useState<JoineryStyleOption[]>(DEFAULT_JOINERY_STYLES);
  const [timberSpecies, setTimberSpecies] = useState<QuestionnaireTimberSpecies[]>(DEFAULT_TIMBER_SPECIES);
  const [portfolioDesigns, setPortfolioDesigns] = useState<Array<{ id: string; title: string; description: string; image: string; footprint?: string }>>([]);
  const [portfolioSectionTitle, setPortfolioSectionTitle] = useState('Explore Full Timber Frame Designs');
  const [inspirationImages, setInspirationImages] = useState<{ id: string; url: string; uploadedAt: Date }[]>([]);
  const [uploadingInspo, setUploadingInspo] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneSaved, setPhoneSaved] = useState(false);
  const [savingPhone, setSavingPhone] = useState(false);
  const [expandedComment, setExpandedComment] = useState<string | null>(null);
  const [commentTexts, setCommentTexts] = useState<Record<string, string>>({});

  // Lightbox state
  const [lightboxImage, setLightboxImage] = useState<{ url: string; title: string; optionId: string } | null>(null);
  const [hasClickedImage, setHasClickedImage] = useState(() => {
    try { return sessionStorage.getItem('dq_image_clicked') === 'true'; } catch { return false; }
  });

  const openLightbox = (url: string, title: string, optionId: string) => {
    setLightboxImage({ url, title, optionId });
    if (!hasClickedImage) {
      setHasClickedImage(true);
      try { sessionStorage.setItem('dq_image_clicked', 'true'); } catch {}
    }
  };

  // Video recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [recordingBlob, setRecordingBlob] = useState<Blob | null>(null);
  const [recordingPreviewUrl, setRecordingPreviewUrl] = useState<string | null>(null);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const videoPreviewRef = useRef<HTMLVideoElement>(null);
  const recordingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const chunksRef = useRef<Blob[]>([]);

  // Joinery carousel state
  const [joineryIndex, setJoineryIndex] = useState(0);

  // Welcome lookbook modal
  const [showWelcome, setShowWelcome] = useState(() => {
    try { return sessionStorage.getItem(`dq_welcome_seen_${portalId}`) !== 'true'; } catch { return true; }
  });
  const dismissWelcome = () => {
    setShowWelcome(false);
    try { sessionStorage.setItem(`dq_welcome_seen_${portalId}`, 'true'); } catch {}
  };

  // Design direction (high-level aesthetic picks)
  const [designDirections, setDesignDirections] = useState<string[]>([]);

  // Species carousel index
  const [speciesIdx, setSpeciesIdx] = useState(0);

  // Fetch or create questionnaire doc — reset state when projectId changes
  useEffect(() => {
    setQuestionnaire(null);
    setQuestionnaireDocId(null);
    setSelections([]);
    setSpecs({});
    setGeneralNotes('');
    setVideos([]);
    setInspirationImages([]);
    setCommentTexts({});
    setSubmitted(false);
    setLoading(true);

    const fetchQuestionnaire = async () => {
      try {
        const ref_ = collection(db, 'designQuestionnaires');
        let constraints;
        if (projectId) {
          constraints = [where('portalId', '==', portalId), where('projectId', '==', projectId)];
        } else {
          constraints = [where('portalId', '==', portalId)];
        }
        const q = query(ref_, ...constraints);
        const snap = await getDocs(q);

        if (!snap.empty) {
          const docSnap = projectId
            ? snap.docs[0]
            : (snap.docs.find(d => !d.data().projectId) || snap.docs[0]);
          const data = docSnap.data();
          setQuestionnaireDocId(docSnap.id);
          const sel: QuestionnaireSelection[] = (data.selections || []).map((s: any) => ({
            ...s,
            selectedAt: s.selectedAt?.toDate?.() || new Date(s.selectedAt),
          }));
          setSelections(sel);
          setSpecs(data.specs || {});
          setGeneralNotes(data.generalNotes || '');
          setSubmitted(!!data.completedAt);
          setVideos((data.videos || []).map((v: any) => ({
            ...v,
            uploadedAt: v.uploadedAt?.toDate?.() || new Date(v.uploadedAt),
          })));
          if (data.designDirections?.length) setDesignDirections(data.designDirections);
          const ct: Record<string, string> = {};
          for (const s of sel) {
            if (s.comment) ct[s.optionId] = s.comment;
          }
          setCommentTexts(ct);
          setQuestionnaire({
            id: docSnap.id,
            ...data,
            selections: sel,
            createdAt: data.createdAt?.toDate?.() || new Date(),
            updatedAt: data.updatedAt?.toDate?.() || new Date(),
            completedAt: data.completedAt?.toDate?.() || undefined,
          } as DQType);
          // Load inspiration images
          if (data.inspirationImages?.length) {
            setInspirationImages(data.inspirationImages.map((img: any) => ({
              ...img,
              uploadedAt: img.uploadedAt?.toDate?.() || new Date(img.uploadedAt),
            })));
          }
        }
      } catch (err) {
        console.error('Error fetching questionnaire:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchQuestionnaire();

    // Also fetch dynamic config
    const fetchConfig = async () => {
      try {
        const snap = await getDoc(doc(db, 'appSettings', 'questionnaire'));
        if (snap.exists()) {
          const data = snap.data();
          if (data.frameStyles?.length) {
            setFrameStyles(data.frameStyles.sort((a: any, b: any) => a.order - b.order).map((s: any) => ({
              id: s.id, title: s.title, description: s.description, image: s.imageUrl || s.image,
            })));
          }
          if (data.joineryStyles?.length) {
            setJoineryStyles(data.joineryStyles.sort((a: any, b: any) => a.order - b.order).map((s: any) => ({
              id: s.id, title: s.title, description: s.description, image: s.imageUrl || s.image,
            })));
          }
          if (data.speciesImages) {
            const imgMap = data.speciesImages as Record<string, string>;
            setTimberSpecies(prev => prev.map(sp => imgMap[sp.id] ? { ...sp, imageUrl: imgMap[sp.id] } : sp));
          }
          if (data.portfolioSectionTitle) setPortfolioSectionTitle(data.portfolioSectionTitle);
          const nameOverrides = (data.kitNameOverrides || {}) as Record<string, string>;
          // Load selected kits for portfolio section
          if (data.selectedKitIds?.length) {
            try {
              const kitsSnap = await getDocs(collection(db, 'shop_catalog'));
              const selectedIds = data.selectedKitIds as string[];
              const kits = kitsSnap.docs
                .filter(d => selectedIds.includes(d.id))
                .map(d => {
                  const k = d.data();
                  // Extract footprint from features array (e.g. "38x44 footprint")
                  const features = (k.features || []) as string[];
                  const fpMatch = features.find((f: string) => /\d+\s*x\s*\d+/i.test(f) && /foot/i.test(f));
                  const footprint = fpMatch ? fpMatch.replace(/footprint/i, '').trim() : undefined;
                  return {
                    id: d.id,
                    title: nameOverrides[d.id] || k.name || 'Timber Frame',
                    description: k.description || '',
                    image: k.thumbnailUrl || k.thumbnail || (k.galleryUrls?.[0]) || '',
                    footprint,
                  };
                });
              setPortfolioDesigns(kits);
            } catch (e) { console.error('Error loading portfolio kits:', e); }
          }
        }
      } catch (err) { console.error('Error loading config:', err); }
    };
    fetchConfig();
  }, [portalId, projectId]);

  // Auto-save debounce
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const persistToFirestore = useCallback(async (
    updatedSelections: QuestionnaireSelection[],
    updatedSpecs: QuestionnaireSpecs,
    updatedNotes: string,
    updatedVideos: QuestionnaireVideo[],
    updatedInspoImages?: { id: string; url: string; uploadedAt: Date }[],
  ) => {
    if (stakeholderViewOnly) return;
    setSaving(true);
    try {
      const dataToSave: Record<string, any> = {
        portalId,
        leadId,
        clientName,
        designDirections,
        selections: updatedSelections.map(s => ({
          ...s,
          selectedAt: s.selectedAt instanceof Date ? Timestamp.fromDate(s.selectedAt) : s.selectedAt,
        })),
        specs: updatedSpecs,
        generalNotes: updatedNotes,
        videos: updatedVideos.map(v => ({
          ...v,
          uploadedAt: v.uploadedAt instanceof Date ? Timestamp.fromDate(v.uploadedAt) : v.uploadedAt,
        })),
        inspirationImages: (updatedInspoImages ?? inspirationImages).map(img => ({
          ...img,
          uploadedAt: img.uploadedAt instanceof Date ? Timestamp.fromDate(img.uploadedAt) : img.uploadedAt,
        })),
        updatedAt: Timestamp.now(),
      };
      if (projectId) dataToSave.projectId = projectId;

      if (questionnaireDocId) {
        await updateDoc(doc(db, 'designQuestionnaires', questionnaireDocId), dataToSave);
      } else {
        const docRef = await addDoc(collection(db, 'designQuestionnaires'), {
          ...dataToSave,
          createdAt: Timestamp.now(),
        });
        setQuestionnaireDocId(docRef.id);
        // Portal + any project row via Admin callable (non-primary rows denied by rules).
        try {
          await savePortalQuestionnaireStatus({
            portalId,
            questionnaireStatus: 'in-progress',
            projectId: projectId || null,
          });
        } catch (portalStatusErr) {
          console.error('[Questionnaire] Failed to write portal questionnaireStatus:', portalStatusErr);
        }
      }
    } catch (err) {
      console.error('Error saving questionnaire:', err);
    } finally {
      setSaving(false);
    }
  }, [portalId, leadId, clientName, projectId, questionnaireDocId, inspirationImages, designDirections, stakeholderViewOnly]);

  const debouncedSave = useCallback((
    sel: QuestionnaireSelection[],
    sp: QuestionnaireSpecs,
    notes: string,
    vids: QuestionnaireVideo[],
  ) => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      persistToFirestore(sel, sp, notes, vids);
    }, 800);
  }, [persistToFirestore]);

  // Toggle design direction
  const handleToggleDirection = (dirId: string) => {
    if (submitted || stakeholderViewOnly) return;
    const updated = designDirections.includes(dirId)
      ? designDirections.filter(d => d !== dirId)
      : [...designDirections, dirId];
    setDesignDirections(updated);
    debouncedSave(selections, specs, generalNotes, videos);
  };

  // Toggle like on a frame style
  const handleToggleLike = (optionId: string) => {
    if (submitted || stakeholderViewOnly) return;
    const existing = selections.find(s => s.optionId === optionId);
    let updated: QuestionnaireSelection[];
    if (existing) {
      if (existing.liked) {
        // Unlike — remove
        updated = selections.filter(s => s.optionId !== optionId);
      } else {
        updated = selections.map(s => s.optionId === optionId ? { ...s, liked: true } : s);
      }
    } else {
      updated = [...selections, {
        categoryId: 'frame-styles',
        optionId,
        liked: true,
        selectedAt: new Date(),
      }];
    }
    setSelections(updated);
    debouncedSave(updated, specs, generalNotes, videos);
  };

  // Save comment for an option
  const handleSaveComment = (optionId: string) => {
    if (submitted || stakeholderViewOnly) return;
    const text = (commentTexts[optionId] || '').trim();
    const existing = selections.find(s => s.optionId === optionId);
    let updated: QuestionnaireSelection[];
    if (existing) {
      updated = selections.map(s => s.optionId === optionId ? { ...s, comment: text || undefined } : s);
    } else {
      updated = [...selections, {
        categoryId: 'frame-styles',
        optionId,
        liked: false,
        comment: text || undefined,
        selectedAt: new Date(),
      }];
    }
    setSelections(updated);
    setExpandedComment(null);
    debouncedSave(updated, specs, generalNotes, videos);
  };

  // Specs change handlers
  const handleSpecChange = (field: keyof QuestionnaireSpecs, value: string) => {
    if (submitted || stakeholderViewOnly) return;
    const updated = { ...specs, [field]: value };
    setSpecs(updated);
    debouncedSave(selections, updated, generalNotes, videos);
  };

  // General notes
  const handleNotesChange = (value: string) => {
    if (submitted || stakeholderViewOnly) return;
    setGeneralNotes(value);
    debouncedSave(selections, specs, value, videos);
  };

  // Inspiration image upload
  const handleInspoUpload = async (files: FileList) => {
    if (submitted || stakeholderViewOnly || !files.length) return;
    setUploadingInspo(true);
    try {
      let docId = questionnaireDocId;
      if (!docId) {
        const docRef = await addDoc(collection(db, 'designQuestionnaires'), {
          portalId, leadId, clientName, ...(projectId ? { projectId } : {}),
          selections: [], createdAt: Timestamp.now(), updatedAt: Timestamp.now(),
        });
        docId = docRef.id;
        setQuestionnaireDocId(docId);
      }

      const newImages: { id: string; url: string; uploadedAt: Date }[] = [];
      for (const file of Array.from(files)) {
        const imgId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const ext = file.name.split('.').pop() || 'jpg';
        const storagePath = `portal-files/${portalId}/questionnaire/inspiration/${imgId}.${ext}`;
        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, file);
        const url = await getDownloadURL(storageRef);
        newImages.push({ id: imgId, url, uploadedAt: new Date() });
      }

      const updated = [...inspirationImages, ...newImages];
      setInspirationImages(updated);
      persistToFirestore(selections, specs, generalNotes, videos, updated);
    } catch (err) {
      console.error('Error uploading inspiration image:', err);
    } finally {
      setUploadingInspo(false);
    }
  };

  const handleDeleteInspo = (imgId: string) => {
    if (submitted || stakeholderViewOnly) return;
    const updated = inspirationImages.filter(i => i.id !== imgId);
    setInspirationImages(updated);
    persistToFirestore(selections, specs, generalNotes, videos, updated);
  };

  // ═══════════════════════════════════════════
  // VIDEO RECORDING
  // ═══════════════════════════════════════════

  const startRecording = async () => {
    if (stakeholderViewOnly) return;
    setCameraError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      streamRef.current = stream;
      if (videoPreviewRef.current) {
        videoPreviewRef.current.srcObject = stream;
        videoPreviewRef.current.play();
      }

      const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
        ? 'video/webm;codecs=vp9'
        : MediaRecorder.isTypeSupported('video/webm')
        ? 'video/webm'
        : 'video/mp4';

      const recorder = new MediaRecorder(stream, { mimeType });
      chunksRef.current = [];

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mimeType });
        setRecordingBlob(blob);
        stream.getTracks().forEach(t => t.stop());
        // Auto-upload to Firebase immediately — preview from download URL
        setUploadingVideo(true);
        (async () => {
          try {
            let docId = questionnaireDocId;
            if (!docId) {
              const docRef = await addDoc(collection(db, 'designQuestionnaires'), {
                portalId, leadId, clientName, ...(projectId ? { projectId } : {}),
                selections: [],
                createdAt: Timestamp.now(),
                updatedAt: Timestamp.now(),
              });
              docId = docRef.id;
              setQuestionnaireDocId(docId);
            }
            const videoId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
            const ext = blob.type.includes('webm') ? 'webm' : 'mp4';
            const storagePath = `portal-files/${portalId}/questionnaire/videos/${videoId}.${ext}`;
            const storageRef = ref(storage, storagePath);
            await uploadBytes(storageRef, blob);
            const url = await getDownloadURL(storageRef);
            setRecordingPreviewUrl(url);
            // Store the videoId for later use
            setRecordingBlob(null);
            (window as any).__pendingVideoData = { id: videoId, url, durationSeconds: recordingTime };
          } catch (err) {
            console.error('Error uploading video:', err);
            setCameraError('Upload failed. Please try again.');
            setRecordingPreviewUrl(null);
          } finally {
            setUploadingVideo(false);
          }
        })();
      };

      mediaRecorderRef.current = recorder;
      recorder.start(1000);
      setIsRecording(true);
      setRecordingTime(0);

      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          if (prev >= 60) {
            stopRecording();
            return 60;
          }
          return prev + 1;
        });
      }, 1000);
    } catch (err: any) {
      setCameraError(err.message || 'Could not access camera');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
    setIsRecording(false);
  };

  const discardRecording = () => {
    setRecordingBlob(null);
    setRecordingPreviewUrl(null);
    setRecordingTime(0);
    (window as any).__pendingVideoData = null;
  };

  const keepVideo = () => {
    const pending = (window as any).__pendingVideoData;
    if (!pending) return;
    const newVideo: QuestionnaireVideo = {
      id: pending.id,
      url: pending.url,
      durationSeconds: pending.durationSeconds,
      uploadedAt: new Date(),
    };
    const updatedVideos = [...videos, newVideo];
    setVideos(updatedVideos);
    discardRecording();
    persistToFirestore(selections, specs, generalNotes, updatedVideos);
  };

  const handleVideoFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('video/')) return;
    setUploadingVideo(true);
    setCameraError(null);
    try {
      let docId = questionnaireDocId;
      if (!docId) {
        const docRef = await addDoc(collection(db, 'designQuestionnaires'), {
          portalId, leadId, clientName, ...(projectId ? { projectId } : {}),
          selections: [],
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        });
        docId = docRef.id;
        setQuestionnaireDocId(docId);
      }
      const videoId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      const ext = file.name.split('.').pop() || 'mp4';
      const storagePath = `portal-files/${portalId}/questionnaire/videos/${videoId}.${ext}`;
      const storageRef = ref(storage, storagePath);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      const newVideo: QuestionnaireVideo = {
        id: videoId, url, durationSeconds: 0, uploadedAt: new Date(),
      };
      const updatedVideos = [...videos, newVideo];
      setVideos(updatedVideos);
      persistToFirestore(selections, specs, generalNotes, updatedVideos);
    } catch (err) {
      console.error('Error uploading video file:', err);
      setCameraError('Upload failed. Please try again.');
    } finally {
      setUploadingVideo(false);
      e.target.value = '';
    }
  };

  const deleteVideo = async (videoId: string) => {
    if (stakeholderViewOnly) return;
    const updatedVideos = videos.filter(v => v.id !== videoId);
    setVideos(updatedVideos);
    persistToFirestore(selections, specs, generalNotes, updatedVideos);
  };

  // ═══════════════════════════════════════════
  // SUBMIT
  // ═══════════════════════════════════════════

  const handleSubmit = async () => {
    if (stakeholderViewOnly) return;
    if (!questionnaireDocId) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, 'designQuestionnaires', questionnaireDocId), {
        completedAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
      try {
        await savePortalQuestionnaireStatus({
          portalId,
          questionnaireStatus: 'completed',
          projectId: projectId || null,
        });
      } catch (portalStatusErr) {
        console.error('[Questionnaire] Failed to write completed questionnaireStatus:', portalStatusErr);
      }
      logPortalActivity(portalId, projectId || null, 'action', 'questionnaire_submitted');
      // Notify employees via Cloud Function (server-side fan-out)
      try {
        const functionsBase = window.location.hostname === 'localhost'
          ? 'http://localhost:5001/lctf-projects/us-central1'
          : 'https://us-central1-lctf-projects.cloudfunctions.net';
        const likedCount = selections.filter(s => s.liked).length;
        await fetch(`${functionsBase}/sendPortalNotification`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            type: 'questionnaire_completed',
            portalId,
            leadId,
            clientName,
            message: `${clientName} completed their design questionnaire — ${likedCount} style(s) selected.`,
          }),
        });
      } catch { /* non-blocking */ }
      setSubmitted(true);
    } catch (err) {
      console.error('Error submitting questionnaire:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleSavePhone = async () => {
    if (stakeholderViewOnly) return;
    if (!phoneNumber.trim() || !leadId) return;
    setSavingPhone(true);
    try {
      const functionsBase = window.location.hostname === 'localhost'
        ? 'http://localhost:5001/lctf-projects/us-central1'
        : 'https://us-central1-lctf-projects.cloudfunctions.net';
      await fetch(`${functionsBase}/sendPortalNotification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'call_requested',
          portalId,
          leadId,
          clientName,
          phone: phoneNumber.trim(),
        }),
      });
      logPortalActivity(portalId, projectId || null, 'action', 'call_requested');
      setPhoneSaved(true);
    } catch (err) {
      console.error('Error saving phone:', err);
    } finally {
      setSavingPhone(false);
    }
  };

  // Helpers
  const isLiked = (optionId: string) => selections.find(s => s.optionId === optionId)?.liked || false;
  const getComment = (optionId: string) => selections.find(s => s.optionId === optionId)?.comment || '';
  const likedCount = selections.filter(s => s.liked).length;

  const formatTime = (secs: number) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return <LoadingScreen message="Loading your questionnaire..." size="medium" />;
  }

  return (
    <div className="dq-container">
      {/* Header */}
      <header className="dq-header">
        {onBack && (
          <button className="dq-back-btn" onClick={onBack}>
            <ArrowLeft size={18} /> {isPublicLead ? 'Back to LC Timber Frames' : 'Back to Portal'}
          </button>
        )}
        <h1 className="dq-title">Help Us Understand Your Vision</h1>
        <p className="dq-subtitle">
          This is just a starting point — browse some styles and let us know what catches your eye. There are no wrong answers. After this, the next step is a design deposit, and then you'll sit down with our team to really dive deep into your style.
        </p>
        {stakeholderViewOnly && (
          <div
            role="status"
            style={{
              marginTop: 12,
              padding: '12px 14px',
              borderRadius: 8,
              background: 'rgba(44,62,47,0.08)',
              border: '1px solid rgba(44,62,47,0.2)',
              fontSize: 13,
              color: '#374151',
            }}
          >
            <strong>View only</strong> — you can browse this questionnaire as a stakeholder; answers and uploads are disabled.
          </div>
        )}
        {saving && <div className="dq-saving-indicator">Saving...</div>}
        {submitted && <div className="dq-submitted-badge">✓ Submitted</div>}
      </header>

      {/* Section 1: Design Direction */}
      <section className="dq-section">
        <h2 className="dq-section-title">
          <span className="dq-section-number">1</span>
          What's your overall style?
        </h2>
        <p className="dq-section-desc">Pick as many as you like — this helps us understand the feel you're going for before we get into the details.</p>

        <div className="dq-direction-grid">
          {[
            { id: 'modern', label: 'Modern', desc: 'Clean lines, open spaces' },
            { id: 'traditional', label: 'Traditional', desc: 'Classic proportions, warm symmetry' },
            { id: 'rustic', label: 'Rustic / Lodge', desc: 'Natural textures, rugged character' },
            { id: 'farmhouse', label: 'Farmhouse / Barn', desc: 'Open volumes, relaxed elegance' },
            { id: 'grand', label: 'Grand / Estate', desc: 'Cathedral ceilings, dramatic scale' },
          ].map(dir => (
            <button
              key={dir.id}
              className={`dq-direction-chip ${designDirections.includes(dir.id) ? 'dq-direction-chip--active' : ''}`}
              onClick={() => handleToggleDirection(dir.id)}
              disabled={submitted}
            >
              <span className="dq-direction-label">{dir.label}</span>
              <span className="dq-direction-desc">{dir.desc}</span>
            </button>
          ))}
        </div>
      </section>

      {/* Section 2: Frame Styles */}
      <section className="dq-section">
        <h2 className="dq-section-title">
          <span className="dq-section-number">2</span>
          What structural style speaks to you?
        </h2>
        <p className="dq-section-desc">These are some of the structural styles we work with. Heart any that catch your eye — this helps us get a sense of your taste before we meet.</p>

        <div className="dq-grid">
          {frameStyles.map(style => (
            <div
              key={style.id}
              className={`dq-card ${isLiked(style.id) ? 'dq-card--liked' : ''}`}
            >
              <div className="dq-card-image" onClick={() => openLightbox(style.image, style.title, style.id)} style={{ cursor: 'pointer' }}>
                <img src={style.image} alt={style.title} loading="lazy" />
                {!hasClickedImage && (
                  <div className="dq-image-hint">
                    <ZoomIn size={16} /> Tap to enlarge
                  </div>
                )}
                <button
                  className={`dq-heart-btn ${isLiked(style.id) ? 'dq-heart-btn--active' : ''}`}
                  onClick={(e) => { e.stopPropagation(); handleToggleLike(style.id); }}
                  disabled={submitted}
                  aria-label={isLiked(style.id) ? 'Unlike' : 'Like'}
                >
                  <Heart size={20} fill={isLiked(style.id) ? 'currentColor' : 'none'} />
                </button>
              </div>
              <div className="dq-card-body">
                <h3 className="dq-card-title">{style.title}</h3>
                <p className="dq-card-desc">{style.description}</p>
                <div className="dq-card-actions">
                  <button
                    className="dq-comment-toggle"
                    onClick={() => setExpandedComment(expandedComment === style.id ? null : style.id)}
                    disabled={submitted}
                  >
                    <MessageSquare size={14} />
                    {getComment(style.id) ? 'Edit comment' : 'Add a comment'}
                  </button>
                </div>
                {expandedComment === style.id && (
                  <div className="dq-comment-box">
                    <textarea
                      value={commentTexts[style.id] || ''}
                      onChange={e => setCommentTexts(prev => ({ ...prev, [style.id]: e.target.value }))}
                      placeholder="What do you like about this style? Any thoughts..."
                      rows={3}
                      disabled={submitted}
                    />
                    <button className="dq-comment-save" onClick={() => handleSaveComment(style.id)} disabled={submitted}>
                      Save
                    </button>
                  </div>
                )}
                {getComment(style.id) && expandedComment !== style.id && (
                  <div className="dq-comment-preview">"{getComment(style.id)}"</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Section 3: Past Designs / Portfolio */}
      {portfolioDesigns.length > 0 && (
        <section className="dq-section">
          <h2 className="dq-section-title">
            <span className="dq-section-number">3</span>
            {portfolioSectionTitle}
          </h2>
          <p className="dq-section-desc">Here are some complete timber frame designs for inspiration. Heart any that resonate with you — we'll use your picks as a starting point when we meet to discuss your project.</p>

          <div className="dq-grid">
            {portfolioDesigns.map(design => (
              <div
                key={design.id}
                className={`dq-card ${isLiked(design.id) ? 'dq-card--liked' : ''}`}
              >
                <div className="dq-card-image" onClick={() => openLightbox(design.image, design.title, design.id)} style={{ cursor: 'pointer' }}>
                  <img src={design.image} alt={design.title} loading="lazy" />
                  {!hasClickedImage && (
                    <div className="dq-image-hint">
                      <ZoomIn size={16} /> Tap to enlarge
                    </div>
                  )}
                  <button
                    className={`dq-heart-btn ${isLiked(design.id) ? 'dq-heart-btn--active' : ''}`}
                    onClick={(e) => { e.stopPropagation(); handleToggleLike(design.id); }}
                    disabled={submitted}
                    aria-label={isLiked(design.id) ? 'Unlike' : 'Like'}
                  >
                    <Heart size={20} fill={isLiked(design.id) ? 'currentColor' : 'none'} />
                  </button>
                </div>
                <div className="dq-card-body">
                  <h3 className="dq-card-title">
                    {design.title}
                    {design.footprint && (
                      <span style={{
                        display: 'inline-block', marginLeft: 8, padding: '2px 8px',
                        background: '#F0FDF4', color: '#166534', fontSize: 11, fontWeight: 600,
                        borderRadius: 6, border: '1px solid #BBF7D0', verticalAlign: 'middle',
                      }}>{design.footprint}</span>
                    )}
                  </h3>
                  <p className="dq-card-desc">{design.description}</p>
                  <div className="dq-card-actions">
                    <button
                      className="dq-comment-toggle"
                      onClick={() => setExpandedComment(expandedComment === design.id ? null : design.id)}
                      disabled={submitted}
                    >
                      <MessageSquare size={14} />
                      {getComment(design.id) ? 'Edit comment' : 'Add a comment'}
                    </button>
                  </div>
                  {expandedComment === design.id && (
                    <div className="dq-comment-box">
                      <textarea
                        value={commentTexts[design.id] || ''}
                        onChange={e => setCommentTexts(prev => ({ ...prev, [design.id]: e.target.value }))}
                        placeholder="What do you like about this project? Any thoughts..."
                        rows={3}
                        disabled={submitted}
                      />
                      <button className="dq-comment-save" onClick={() => handleSaveComment(design.id)} disabled={submitted}>
                        Save
                      </button>
                    </div>
                  )}
                  {getComment(design.id) && expandedComment !== design.id && (
                    <div className="dq-comment-preview">"{getComment(design.id)}"</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}



      {/* Species Section */}
      <section className="dq-section">
        <h2 className="dq-section-title">
          <span className="dq-section-number">{portfolioDesigns.length > 0 ? '4' : '3'}</span>
          Get to Know the Wood
        </h2>
        <p className="dq-section-desc">
          Every timber species has its own personality. Select a swatch to explore — your designer will walk you through everything in detail.
        </p>

        <div className="dq-sp-tabs">
          {timberSpecies.map((s, i) => (
            <button
              key={s.id}
              className={`dq-sp-tab ${i === speciesIdx ? 'dq-sp-tab--active' : ''}`}
              onClick={() => setSpeciesIdx(i)}
            >
              {s.name.split(' ').pop()}
            </button>
          ))}
        </div>

        {(() => {
          const sp = timberSpecies[speciesIdx];
          return (
            <div className="dq-sp-card" key={sp.id}>
              {sp.imageUrl && (
                <div className="dq-sp-image">
                  <img src={sp.imageUrl} alt={`${sp.name} timber frame`} />
                </div>
              )}
              <div className="dq-sp-content">
                <div className="dq-sp-header">
                  <div>
                    <div className="dq-sp-name-row">
                      <span className="dq-sp-name">{sp.name}</span>
                      <span className="dq-sp-cost">{sp.cost}</span>
                    </div>
                    <span className="dq-sp-latin">{sp.latin}</span>
                  </div>
                  <div className="dq-sp-attr-row">
                    <div className="dq-sp-attr"><span className="dq-sp-attr-l">Hardness</span><span className="dq-sp-attr-v">{sp.hardness}</span></div>
                    <div className="dq-sp-attr"><span className="dq-sp-attr-l">Grain</span><span className="dq-sp-attr-v">{sp.grain}</span></div>
                    <div className="dq-sp-attr"><span className="dq-sp-attr-l">Durability</span><span className="dq-sp-attr-v">{sp.durability}</span></div>
                  </div>
                </div>
                <p className="dq-sp-desc">{sp.description}</p>
                <div className="dq-sp-tags">
                  {sp.pros.map((pro, pi) => (
                    <span key={`p${pi}`} className="dq-sp-tag dq-sp-tag--pro">✓ {pro}</span>
                  ))}
                  {sp.cons.map((con, ci) => (
                    <span key={`c${ci}`} className="dq-sp-tag dq-sp-tag--con">△ {con}</span>
                  ))}
                </div>
              </div>
            </div>
          );
        })()}
      </section>

      {/* Specs Section */}
      <section className="dq-section">
        <h2 className="dq-section-title">
          <span className="dq-section-number">{portfolioDesigns.length > 0 ? '5' : '4'}</span>
          Project Details
        </h2>
        <p className="dq-section-desc">
          If you already have specifics in mind, share them here — otherwise we'll figure this out together.
        </p>

        <div className="dq-specs-grid">
          <div className="dq-spec-field">
            <label><Ruler size={14} /> Footprint</label>
            <input
              type="text"
              placeholder="e.g. 24' × 36'"
              value={specs.footprint || ''}
              onChange={e => handleSpecChange('footprint', e.target.value)}
              disabled={submitted}
            />
          </div>
          <div className="dq-spec-field">
            <label>Roof Pitch</label>
            <input
              type="text"
              placeholder="e.g. 8/12"
              value={specs.roofPitch || ''}
              onChange={e => handleSpecChange('roofPitch', e.target.value)}
              disabled={submitted}
            />
          </div>
          <div className="dq-spec-field">
            <label>Ceiling Height</label>
            <input
              type="text"
              placeholder="e.g. 10'"
              value={specs.ceilingHeight || ''}
              onChange={e => handleSpecChange('ceilingHeight', e.target.value)}
              disabled={submitted}
            />
          </div>
          <div className="dq-spec-field">
            <label>Floors</label>
            <input
              type="text"
              placeholder="e.g. 2"
              value={specs.floors || ''}
              onChange={e => handleSpecChange('floors', e.target.value)}
              disabled={submitted}
            />
          </div>
          <div className="dq-spec-field dq-spec-field--wide">
            <label>Additional Notes</label>
            <textarea
              placeholder="Any other details about your project..."
              value={specs.notes || ''}
              onChange={e => handleSpecChange('notes', e.target.value)}
              rows={3}
              disabled={submitted}
            />
          </div>
        </div>
      </section>

      {/* Section: Video Message */}
      <section className="dq-section">
        <h2 className="dq-section-title">
          <span className="dq-section-number">{portfolioDesigns.length > 0 ? '6' : '5'}</span>
          Record a Video Message
          <span className="dq-optional-badge">If you'd like</span>
        </h2>
        <p className="dq-section-desc">
          Sometimes it's easier to talk it out. Record a quick 30–60 second video sharing your thoughts, ideas, or questions.
        </p>

        <div className="dq-video-area">
          {/* Existing videos */}
          {videos.map(v => (
            <div key={v.id} className="dq-video-item">
              <video src={v.url} controls className="dq-video-player" />
              <div className="dq-video-meta">
                <span>{formatTime(v.durationSeconds)}</span>
                {!submitted && (
                  <button className="dq-video-delete" onClick={() => deleteVideo(v.id)}>
                    <Trash2 size={14} /> Remove
                  </button>
                )}
              </div>
            </div>
          ))}

          {/* Recording UI */}
          {!submitted && !recordingPreviewUrl && !uploadingVideo && (
            <div className="dq-recorder">
              <video ref={videoPreviewRef} className={`dq-recorder-preview ${isRecording ? 'dq-recorder-preview--active' : ''}`} muted playsInline />
              {isRecording ? (
                <div className="dq-recorder-controls">
                  <div className="dq-recording-indicator">
                    <span className="dq-rec-dot" /> {formatTime(recordingTime)} / 1:00
                  </div>
                  <button className="dq-stop-btn" onClick={stopRecording}>
                    <StopCircle size={20} /> Stop Recording
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <button className="dq-record-btn" onClick={startRecording}>
                    <Video size={18} /> Record Video
                  </button>
                  <span style={{ fontSize: 13, color: '#9CA3AF' }}>or</span>
                  <label className="dq-btn-secondary" style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                    <Upload size={14} /> Upload a video
                    <input type="file" accept="video/*" onChange={handleVideoFileUpload} style={{ display: 'none' }} />
                  </label>
                </div>
              )}
              {cameraError && <p className="dq-camera-error">{cameraError}</p>}
            </div>
          )}

          {/* Uploading spinner */}
          {uploadingVideo && !recordingPreviewUrl && (
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12,
              padding: '32px 20px', background: '#FAFAF8', border: '1px solid #E8E5E0',
              borderRadius: 10
            }}>
              <Loader2 size={28} className="dq-spinner" style={{ color: '#2C3E2F', animation: 'spin 1s linear infinite' }} />
              <p style={{ margin: 0, fontSize: 14, color: '#6B7280' }}>Uploading your video...</p>
            </div>
          )}

          {/* Preview from Firebase URL */}
          {recordingPreviewUrl && recordingPreviewUrl !== 'ready' && (
            <div className="dq-recording-review">
              <video src={recordingPreviewUrl} controls playsInline className="dq-video-player" />
              <div className="dq-recording-review-actions">
                <button className="dq-btn-secondary" onClick={discardRecording}>
                  <Trash2 size={14} /> Discard
                </button>
                <button className="dq-btn-primary" onClick={keepVideo}>
                  ✓ Keep Video
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Section: General Notes + Inspiration Images + Submit */}
      <section className="dq-section dq-section--final">
        <h2 className="dq-section-title">
          <span className="dq-section-number">{portfolioDesigns.length > 0 ? '7' : '6'}</span>
          Anything Else?
        </h2>
        <p className="dq-section-desc">
          Share any other thoughts, questions, or upload inspiration photos you've found online.
        </p>

        <textarea
          className="dq-general-notes"
          placeholder="I've always loved the look of timber frames in mountain lodges..."
          value={generalNotes}
          onChange={e => handleNotesChange(e.target.value)}
          rows={5}
          disabled={submitted}
        />

        {/* Inspiration Image Upload */}
        <div style={{ marginTop: 16 }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, color: '#1a1a1a', marginBottom: 8 }}>Inspiration Photos</h3>
          <p style={{ fontSize: 13, color: '#6B7280', marginBottom: 12 }}>Upload any images that capture the look or feel you're going for.</p>
          
          {inspirationImages.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 10, marginBottom: 12 }}>
              {inspirationImages.map(img => (
                <div key={img.id} style={{ position: 'relative', borderRadius: 8, overflow: 'hidden', aspectRatio: '1', background: '#F3F4F6' }}>
                  <img src={img.url} alt="Inspiration" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  {!submitted && (
                    <button
                      onClick={() => handleDeleteInspo(img.id)}
                      style={{
                        position: 'absolute', top: 4, right: 4,
                        width: 24, height: 24, borderRadius: '50%',
                        background: 'rgba(0,0,0,0.6)', color: '#fff',
                        border: 'none', cursor: 'pointer',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}

          {!submitted && (
            <label style={{
              display: 'inline-flex', alignItems: 'center', gap: 8,
              padding: '10px 20px', border: '2px dashed #D1D5DB', borderRadius: 10,
              cursor: 'pointer', color: '#6B7280', fontSize: 14, fontWeight: 500,
              transition: 'border-color 0.2s, color 0.2s',
            }}>
              <Upload size={18} />
              {uploadingInspo ? 'Uploading...' : 'Upload Photos'}
              <input
                type="file"
                accept="image/*"
                multiple
                style={{ display: 'none' }}
                onChange={e => { if (e.target.files) handleInspoUpload(e.target.files); e.target.value = ''; }}
                disabled={uploadingInspo}
              />
            </label>
          )}
        </div>

        {!submitted && (
          <div className="dq-submit-area">
            <div className="dq-submit-summary">
              {likedCount > 0 ? (
                <span>{likedCount} style{likedCount > 1 ? 's' : ''} selected — great picks!</span>
              ) : (
                <span>No styles selected yet — that's totally fine, we'll explore options together.</span>
              )}
            </div>
            <button
              className="dq-submit-btn"
              onClick={handleSubmit}
              disabled={saving}
            >
              <Send size={16} /> Submit & Continue
            </button>
          </div>
        )}

        {submitted && isPublicLead && (
          <div className="dq-thank-you">
            <h3>Thanks for exploring{clientName ? `, ${clientName.split(' ')[0]}` : ''}!</h3>
            <p>Our team has your selections and will use them to understand your style.</p>
            {!phoneSaved ? (
              <>
                <div style={{ width: 40, height: 2, background: 'var(--dq-border)', margin: '18px auto', borderRadius: 1 }} />
                <p style={{ fontSize: 15, fontWeight: 600, color: 'var(--dq-text)', margin: '0 0 14px' }}>
                  Want to talk through your picks?
                </p>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'center', maxWidth: 320, margin: '0 auto' }}>
                  <input
                    type="tel"
                    placeholder="Your phone number"
                    value={phoneNumber}
                    onChange={e => setPhoneNumber(e.target.value)}
                    style={{
                      flex: 1, padding: '11px 14px', fontSize: 14,
                      border: '1.5px solid var(--dq-border)', borderRadius: 8,
                      fontFamily: 'var(--dq-font)', outline: 'none', background: '#fff',
                    }}
                  />
                  <button
                    onClick={handleSavePhone}
                    disabled={!phoneNumber.trim() || savingPhone}
                    style={{
                      padding: '11px 22px', fontSize: 14, fontWeight: 600,
                      background: 'var(--dq-green)', color: '#fff', border: 'none', borderRadius: 8,
                      cursor: phoneNumber.trim() ? 'pointer' : 'not-allowed',
                      opacity: phoneNumber.trim() ? 1 : 0.5,
                      whiteSpace: 'nowrap', transition: 'opacity 0.15s',
                    }}
                  >
                    {savingPhone ? 'Sending...' : 'Request a Call'}
                  </button>
                </div>
                <p style={{ fontSize: 12, color: 'var(--dq-text-soft)', marginTop: 10 }}>We'll reach out at a time that works for you.</p>
              </>
            ) : (
              <>
                <div style={{ width: 40, height: 2, background: 'var(--dq-border)', margin: '18px auto', borderRadius: 1 }} />
                <p style={{ fontSize: 15, fontWeight: 600, color: 'var(--dq-text)', margin: 0 }}>
                  We'll be in touch soon.
                </p>
              </>
            )}
          </div>
        )}

        {submitted && !isPublicLead && (
          <div className="dq-thank-you">
            <h3>Thank you, {clientName.split(' ')[0]}!</h3>
            <p>We've received your design preferences. The next step is a design deposit — head back to your portal to review design options and complete payment. Once that's done, you'll sit down with our team for a full style consultation.</p>
            {onBack && (
              <button
                onClick={onBack}
                style={{
                  marginTop: 16, display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '12px 24px', fontSize: 15, fontWeight: 600,
                  background: '#2C3E2F', color: '#fff', border: 'none', borderRadius: 8,
                  cursor: 'pointer', transition: 'opacity 0.15s',
                }}
                onMouseOver={e => (e.currentTarget.style.opacity = '0.9')}
                onMouseOut={e => (e.currentTarget.style.opacity = '1')}
              >
                Back to Portal <ChevronRight size={16} />
              </button>
            )}
          </div>
        )}
      </section>

      {/* Lightbox */}
      {lightboxImage && (
        <div
          className="dq-lightbox"
          onClick={() => setLightboxImage(null)}
          onKeyDown={e => { if (e.key === 'Escape') setLightboxImage(null); }}
          tabIndex={0}
          ref={el => el?.focus()}
        >
          <button className="dq-lightbox-close" onClick={() => setLightboxImage(null)}>
            <X size={24} />
          </button>
          <img
            src={lightboxImage.url}
            alt={lightboxImage.title}
            className="dq-lightbox-img"
            onClick={e => e.stopPropagation()}
          />
          <div className="dq-lightbox-footer" onClick={e => e.stopPropagation()}>
            <div className="dq-lightbox-caption">{lightboxImage.title}</div>
            <div className="dq-lightbox-actions">
              <button
                className={`dq-lightbox-heart ${isLiked(lightboxImage.optionId) ? 'dq-lightbox-heart--active' : ''}`}
                onClick={() => handleToggleLike(lightboxImage.optionId)}
                disabled={submitted}
              >
                <Heart size={20} fill={isLiked(lightboxImage.optionId) ? 'currentColor' : 'none'} />
                {isLiked(lightboxImage.optionId) ? 'Liked' : 'Like this style'}
              </button>
            </div>
            <div className="dq-lightbox-comment">
              <textarea
                value={commentTexts[lightboxImage.optionId] || getComment(lightboxImage.optionId) || ''}
                onChange={e => setCommentTexts(prev => ({ ...prev, [lightboxImage.optionId]: e.target.value }))}
                placeholder="What do you like about this? Any thoughts..."
                rows={2}
                disabled={submitted}
              />
              {(commentTexts[lightboxImage.optionId] || '').trim() && (
                <button
                  className="dq-lightbox-save-comment"
                  onClick={() => { handleSaveComment(lightboxImage.optionId); }}
                  disabled={submitted}
                >
                  Save Comment
                </button>
              )}
              {getComment(lightboxImage.optionId) && !commentTexts[lightboxImage.optionId] && (
                <div className="dq-lightbox-saved-comment">✓ Comment saved</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Welcome Lookbook Modal */}
      {showWelcome && !loading && (
        <div className="dq-welcome-overlay" onClick={dismissWelcome}>
          <div className="dq-welcome-modal" onClick={e => e.stopPropagation()}>
            <div className="dq-welcome-body">
              <div className="dq-welcome-eyebrow">Your Personal Lookbook</div>
              <h2 className="dq-welcome-heading">
                Explore timber frame styles
              </h2>
              <p className="dq-welcome-text">
                Browse styles, learn about different woods, and heart anything that catches your eye. No wrong answers, no pressure.
              </p>
              <div className="dq-welcome-bullets">
                <div className="dq-welcome-bullet">
                  <div className="dq-welcome-bullet-icon"><Heart size={14} /></div>
                  <span><strong>Heart styles you love</strong> — or don't. We'll figure out your perfect design together either way.</span>
                </div>
                <div className="dq-welcome-bullet">
                  <div className="dq-welcome-bullet-icon"><TreePine size={14} /></div>
                  <span><strong>Learn about timber species</strong> — each wood has its own character, warmth, and story.</span>
                </div>
                <div className="dq-welcome-bullet">
                  <div className="dq-welcome-bullet-icon"><MessageSquare size={14} /></div>
                  <span><strong>Leave notes if you'd like</strong> — tell us what you love or what questions you have.</span>
                </div>
              </div>
              <button className="dq-welcome-cta" onClick={dismissWelcome}>
                Start Browsing <ChevronRight size={18} />
              </button>
              <button className="dq-welcome-dismiss" onClick={dismissWelcome}>
                I've been here before — skip
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DesignQuestionnaire;
